import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchInvoicingCart,
  saveInvoicingCartByKey,
  fetchInvoicingCartDiff,
  fetchCartSnapshots,
  fetchDocumentSnapshots,
} from '../../../../../services/orders';
import { CART_WORKSPACE_SOURCES, normalizeCartPayload, buildOrderForCartView, selectCartPayloadForSource } from './cartWorkspaceModel';
import {
  addProductToPayload,
  addSectionToPayload,
  updateProductInPayloadWithResult,
  deleteProductFromPayload,
  copyProductInPayload,
  reorderProductsInPayload,
  updateSectionInPayload,
  deleteSectionFromPayload,
  reorderCartLayoutInPayload,
  addOtherServiceToPayload,
  updateOtherServiceInPayload,
  deleteOtherServiceFromPayload,
  addAdjustmentToPayload,
  updateAdjustmentInPayload,
  deleteAdjustmentFromPayload,
} from './cartWorkspaceMutations';

const EMPTY_PAYLOAD = Object.freeze({ Products: [], OtherService: [], Adjustments: [], cartSections: [], layout: [], meta: {} });

const SALES_ORDER_SOURCE = CART_WORKSPACE_SOURCES.SALES_ORDER;
const INVOICING_SOURCE = CART_WORKSPACE_SOURCES.INVOICING;
const ORDER_SOURCE = CART_WORKSPACE_SOURCES.ORDER;
const SALES_ORDER_TYPES = new Set(['sales-order', 'sales_order', 'salesorder']);
const SALES_ORDER_CART_READY_STATUSES = new Set(['3', '6', '7', '8', '9']);

const getSnapshotDocumentType = (snapshot = {}) =>
  String(
    snapshot?.documentType ||
    snapshot?.status ||
    snapshot?.type ||
    snapshot?.snapshotType ||
    ''
  ).trim().toLowerCase();

const extractSnapshotPayload = (snapshot = {}) =>
  snapshot?.payload ||
  snapshot?.snapshot?.payload ||
  snapshot?.snapshot ||
  null;

export const resolveExactDocumentSnapshotPayload = ({
  metadataSnapshot = {},
  fullRows = [],
} = {}) => {
  const rows = Array.isArray(fullRows) ? fullRows : [];
  const snapshotId = String(
    metadataSnapshot?.snapshotId ||
    metadataSnapshot?.cartSnapshotId ||
    ''
  ).trim();
  const revisionNumber = Number(metadataSnapshot?.revisionNumber || 0);

  const exactById = snapshotId
    ? rows.find((row) =>
        String(row?.snapshotId || row?.cartSnapshotId || '').trim() === snapshotId
      )
    : null;

  if (exactById) return extractSnapshotPayload(exactById);

  const exactByRevision = revisionNumber > 0
    ? rows.find((row) => Number(row?.revisionNumber || 0) === revisionNumber)
    : null;

  return exactByRevision ? extractSnapshotPayload(exactByRevision) : null;
};

const isSalesOrderCartReadyStatus = (status) =>
  SALES_ORDER_CART_READY_STATUSES.has(String(status || '').split('_')[0]);

export const selectActiveSalesOrderCartSnapshot = ({ snapshots = [], documentId = null } = {}) => {
  const selectedDocumentId = String(documentId ?? '').trim();
  return (Array.isArray(snapshots) ? snapshots : [])
    .filter((snapshot) => SALES_ORDER_TYPES.has(getSnapshotDocumentType(snapshot)))
    .filter((snapshot) => String(snapshot?.lifecycleStatus || '').toLowerCase() === 'active')
    .filter((snapshot) => !selectedDocumentId ||
      String(snapshot?.documentId ?? snapshot?.estimationId ?? '') === selectedDocumentId)
    .sort((a, b) => {
      const revDiff = Number(b?.revisionNumber || 0) - Number(a?.revisionNumber || 0);
      if (revDiff) return revDiff;
      return new Date(b?.updatedAt || b?.createdAt || 0) - new Date(a?.updatedAt || a?.createdAt || 0);
    })[0] || null;
};


const resolveProductIdentityKey = (product = {}) =>
  String(
    product?.orderItemId ||
    product?.OrderItemId ||
    product?.id ||
    product?.ProductId ||
    product?.productId ||
    ''
  );

export const shouldCommitSalesOrderCartLoad = ({
  requestId,
  currentRequestId,
  loadVersion,
  currentMutationVersion,
}) => (
  requestId === currentRequestId &&
  loadVersion === currentMutationVersion
);

export const deriveWorkspaceProductEdit = (payload, identity, patchOrProduct) => {
  const result = updateProductInPayloadWithResult(payload, identity, patchOrProduct);
  if (!result.matched) {
    return {
      ok: false,
      reason: 'product-identity-not-found',
      matchStrategy: result.matchStrategy,
      matchedIndex: result.matchedIndex,
    };
  }
  return { ok: true, payload: result.payload };
};

export const hydrateCartProductTaxDetails = (products = [], order = {}) => {
  const sourceProducts = Array.isArray(order?.Products)
    ? order.Products
    : Array.isArray(order?.Items)
      ? order.Items
      : [];

  const byIdentity = new Map();
  const byTaxId = new Map();

  sourceProducts.forEach((row) => {
    const identity = resolveProductIdentityKey(row);
    if (identity && row?.taxDetails && !byIdentity.has(identity)) {
      byIdentity.set(identity, row.taxDetails);
    }
    const taxId = String(row?.Tax ?? row?.tax ?? row?.taxDetails?.taxId ?? '');
    if (taxId && row?.taxDetails && !byTaxId.has(taxId)) {
      byTaxId.set(taxId, row.taxDetails);
    }
  });

  return (products || []).map((product) => {
    if (product?.taxDetails?.rate != null || product?.taxDetails?.Rate != null) return product;

    const identity = resolveProductIdentityKey(product);
    const fromIdentity = identity ? byIdentity.get(identity) : null;
    const taxId = String(product?.Tax ?? product?.tax ?? '');
    const fromTaxId = taxId ? byTaxId.get(taxId) : null;
    const resolvedTaxDetails = fromIdentity || fromTaxId || null;

    if (!resolvedTaxDetails) return product;
    return { ...product, taxDetails: resolvedTaxDetails };
  });
};

export default function useCartWorkspace({
  orderId,
  order,
  cartView,
  salesOrderDocumentId = null,
  fetchOrder,
  invoicingCartKey = null,
  salesOrderCartUnlocked = false,
  enabled = true,
}) {
  const source = cartView || ORDER_SOURCE;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [missing, setMissing] = useState(false);
  const [cartPayload, setCartPayload] = useState(EMPTY_PAYLOAD);
  const [salesOrderPayload, setSalesOrderPayload] = useState(EMPTY_PAYLOAD);
  const [dirty, setDirty] = useState(false);
  const displayedPayload = useMemo(
    () => selectCartPayloadForSource(cartPayload, source),
    [cartPayload, source]
  );
  const sourceChangePending = displayedPayload !== cartPayload;
  const editable = (source !== SALES_ORDER_SOURCE || salesOrderCartUnlocked) &&
    !sourceChangePending && !loading && !missing;
  const cartPayloadRef = useRef(EMPTY_PAYLOAD);
  const mutationVersionRef = useRef(0);
  const salesOrderLoadRequestRef = useRef(0);
  const invoicingRevisionRef = useRef(null);
  const invoicingLoadRequestRef = useRef(0);
  const [loadedInvoicingCart, setLoadedInvoicingCart] = useState({ orderId: null, key: null });
  const enabledRef = useRef(enabled);
  const sourceRef = useRef(source);
  const invoiceCartKeyRef = useRef(invoicingCartKey);
  const activeOrderIdRef = useRef(orderId);
  enabledRef.current = enabled;
  sourceRef.current = source;
  invoiceCartKeyRef.current = invoicingCartKey;
  activeOrderIdRef.current = orderId;

  useEffect(() => {
    cartPayloadRef.current = cartPayload;
  }, [cartPayload]);

  const liveOrderPayload = useMemo(() => normalizeCartPayload({
    Products: order?.Products || order?.Items || [],
    OtherService: order?.OtherService || [],
    cartSections: order?.cartSections || [],
    meta: { source: ORDER_SOURCE },
  }), [order]);

  useEffect(() => {
    const documentRows = Array.isArray(order?.SalesOrderData) ? order.SalesOrderData : [];
    const scopedRows = salesOrderDocumentId
      ? documentRows.filter((so) => String(so?.estimationId ?? so?.id ?? '') === String(salesOrderDocumentId))
      : documentRows;
    const latestCompleted = scopedRows.length
      ? [...scopedRows].sort((a, b) => new Date(b?.updatedAt || 0) - new Date(a?.updatedAt || 0))[0]
      : null;

    const raw = latestCompleted?.stampedBookedAmount;
    let stamp = raw;

    if (typeof raw === 'string') {
      try {
        stamp = JSON.parse(raw);
      } catch {
        stamp = {};
      }
    }

    // Never borrow a different document's embedded Sales Order Cart snapshot.
    const fromSnapshot = stamp?.cartSnapshot || (!salesOrderDocumentId ? order?.salesOrderCartSnapshot : null) || null;

    const soPayload = normalizeCartPayload(
      fromSnapshot,
      { meta: { source: SALES_ORDER_SOURCE } },
      SALES_ORDER_SOURCE
    );

    setSalesOrderPayload(soPayload);
  }, [order, salesOrderDocumentId]);

  const loadSalesOrderCart = useCallback(async () => {
    if (!enabledRef.current || !orderId) return null;

    const loadVersion = mutationVersionRef.current;
    const requestId = ++salesOrderLoadRequestRef.current;
    const isCurrentLoad = () => enabledRef.current && shouldCommitSalesOrderCartLoad({
      requestId,
      currentRequestId: salesOrderLoadRequestRef.current,
      loadVersion,
      currentMutationVersion: mutationVersionRef.current,
    });

    setError('');

    try {
      setLoading(true);

      const res = await fetchCartSnapshots(orderId);
      const response = res?.data || res;

      const snapshots = Array.isArray(response?.snapshots)
        ? response.snapshots
        : Array.isArray(response)
          ? response
          : [];

      const salesOrderSnapshot = selectActiveSalesOrderCartSnapshot({
        snapshots,
        documentId: salesOrderDocumentId,
      });

      const eligibleSalesOrders = (Array.isArray(order?.SalesOrderData) ? order.SalesOrderData : [])
        .filter((so) => isSalesOrderCartReadyStatus(so?.status))
        .filter((so) => String(so?.lifecycleStatus || '').toLowerCase() === 'active');
      const activeSnapshotDocId = String(salesOrderSnapshot?.documentId || salesOrderSnapshot?.estimationId || '');
      const hasEligibleRecord = activeSnapshotDocId
        ? eligibleSalesOrders.some((so) => String(so?.estimationId || so?.id || '') === activeSnapshotDocId)
        : eligibleSalesOrders.length > 0;
      if (!hasEligibleRecord || !salesOrderSnapshot) {
        if (!isCurrentLoad()) return cartPayloadRef.current;

        const message = 'Sales Order Cart unavailable until Sales Order reaches approved/booked status.';
        setError(message);
        // Do not show the full project Order Cart under a Sales Order Cart label.
        const unavailable = normalizeCartPayload({}, { meta: { source: SALES_ORDER_SOURCE } }, SALES_ORDER_SOURCE);
        setCartPayload(unavailable);
        setSalesOrderPayload(unavailable);
        setMissing(true);
        setDirty(false);
        return null;
      }

      let rawPayload = extractSnapshotPayload(salesOrderSnapshot);

      // /cart-snapshots can return metadata-only rows (no payload). If so, hydrate from document-snapshots.
      if (!rawPayload && salesOrderSnapshot) {
        const documentId = salesOrderSnapshot?.documentId || salesOrderSnapshot?.estimationId;
        if (documentId) {
          try {
            const fullRes = await fetchDocumentSnapshots(orderId, 'sales-order', documentId);
            const fullRows = Array.isArray(fullRes?.snapshots)
              ? fullRes.snapshots
              : Array.isArray(fullRes)
                ? fullRes
                : [];

            rawPayload = resolveExactDocumentSnapshotPayload({
              metadataSnapshot: salesOrderSnapshot,
              fullRows,
            });

            if (!rawPayload) {
              throw new Error('The exact active Sales Order snapshot payload could not be resolved.');
            }
          } catch (hydrateErr) {
            console.warn('[useCartWorkspace sales-order-cart] payload hydration failed:', hydrateErr?.message || hydrateErr);
            throw hydrateErr;
          }
        }
      }

      const payload = normalizeCartPayload(
        rawPayload,
        { meta: { source: SALES_ORDER_SOURCE } },
        SALES_ORDER_SOURCE
      );
      payload.Products = hydrateCartProductTaxDetails(payload.Products, order);

      if (!isCurrentLoad()) return cartPayloadRef.current;

      setSalesOrderPayload(payload);
      setCartPayload(payload);
      setMissing(!salesOrderSnapshot);
      setDirty(false);

      return payload;
    } catch (err) {
      console.warn('[useCartWorkspace sales-order-cart] failed:', err?.message || err);

      if (!isCurrentLoad()) return cartPayloadRef.current;

      // Fail closed. Never leave the previous Order Cart's contents under a
      // Sales Order Cart label when the exact approved snapshot cannot load.
      const unavailable = normalizeCartPayload(
        {},
        { meta: { source: SALES_ORDER_SOURCE } },
        SALES_ORDER_SOURCE
      );
      cartPayloadRef.current = unavailable;
      setCartPayload(unavailable);
      setSalesOrderPayload(unavailable);
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Unable to load the exact Sales Order Cart snapshot.'
      );
      setMissing(true);
      setDirty(false);

      return null;
    } finally {
      if (requestId === salesOrderLoadRequestRef.current) {
        setLoading(false);
      }
    }
  }, [orderId, order, liveOrderPayload, salesOrderDocumentId]);

  const loadInvoicingCart = useCallback(async () => {
    if (!enabledRef.current || !orderId || sourceRef.current !== INVOICING_SOURCE) return null;
    const requestId = ++invoicingLoadRequestRef.current;
    const isCurrent = () => enabledRef.current &&
      sourceRef.current === INVOICING_SOURCE &&
      String(activeOrderIdRef.current) === String(orderId) &&
      invoiceCartKeyRef.current === invoicingCartKey &&
      invoicingLoadRequestRef.current === requestId;

    const loadVersion = mutationVersionRef.current;
    setError('');
    setLoadedInvoicingCart({ orderId: null, key: null });

    // Never look up a project-wide "latest" cart or create a cart on open.
    if (!invoicingCartKey) {
      setMissing(true);
      setLoading(false);
      setCartPayload(normalizeCartPayload({}, undefined, INVOICING_SOURCE));
      if (mutationVersionRef.current === loadVersion) setDirty(false);
      return null;
    }

    try {
      setLoading(true);
      const res = await fetchInvoicingCart(orderId, invoicingCartKey);
      if (!isCurrent()) return null;
      const response = res?.data || res;
      const rawCart = response?.cart || null;
      if (!rawCart || String(rawCart?.documentId || '') !== String(invoicingCartKey)) {
        throw new Error('The selected invoice-owned cart could not be verified.');
      }
      const payload = normalizeCartPayload(
        rawCart?.payload || {},
        { meta: { source: INVOICING_SOURCE, invoicingCartKey } },
        INVOICING_SOURCE
      );
      payload.Products = hydrateCartProductTaxDetails(payload.Products, order);
      invoicingRevisionRef.current = Number(rawCart?.revisionNumber || 0) || null;
      cartPayloadRef.current = payload;
      setCartPayload(payload);
      setMissing(false);
      setLoadedInvoicingCart({ orderId: String(orderId), key: invoicingCartKey });
      if (mutationVersionRef.current === loadVersion) setDirty(false);
      return payload;
    } catch (err) {
      if (!isCurrent()) return null;
      const notFound = Number(err?.response?.status) === 404;
      setLoadedInvoicingCart({ orderId: null, key: null });
      setMissing(notFound);
      setCartPayload(normalizeCartPayload({}, undefined, INVOICING_SOURCE));
      if (mutationVersionRef.current === loadVersion) setDirty(false);
      if (!notFound) setError(err?.response?.data?.message || 'Failed to load Invoicing Cart.');
      return null;
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [orderId, order, invoicingCartKey]);

  const reload = useCallback(async () => {
    if (!enabledRef.current) return null;
    if (!orderId && source !== ORDER_SOURCE) return null;

    setError('');

    if (source === ORDER_SOURCE) {
      if (typeof fetchOrder === 'function') await fetchOrder();

      const payload = normalizeCartPayload({
        Products: order?.Products || order?.Items || [],
        OtherService: order?.OtherService || [],
        cartSections: order?.cartSections || [],
        meta: { source: ORDER_SOURCE },
      });

      setCartPayload(payload);
      setMissing(false);
      setDirty(false);

      return payload;
    }

    if (source === SALES_ORDER_SOURCE) {
      return loadSalesOrderCart();
    }

    return loadInvoicingCart();
  }, [fetchOrder, loadInvoicingCart, loadSalesOrderCart, order, orderId, source]);

  useEffect(() => {
    if (!enabled) {
      // Suppress stale Sales Order snapshots and stop workspace loading when
      // the Invoice list has no verified, invoice-owned Invoicing Cart.
      salesOrderLoadRequestRef.current += 1;
      invoicingLoadRequestRef.current += 1;
      setLoadedInvoicingCart({ orderId: null, key: null });
      setCartPayload(EMPTY_PAYLOAD);
      setSalesOrderPayload(EMPTY_PAYLOAD);
      setLoading(false);
      setMissing(true);
      setError('');
      setDirty(false);
      return;
    }
    if (source === ORDER_SOURCE) {
      invoicingLoadRequestRef.current += 1;
      setLoadedInvoicingCart({ orderId: null, key: null });
      setCartPayload(liveOrderPayload);
      setLoading(false);
      setMissing(false);
      setError('');
      setDirty(false);
      return;
    }

    if (source === SALES_ORDER_SOURCE) {
      invoicingLoadRequestRef.current += 1;
      setLoadedInvoicingCart({ orderId: null, key: null });
      loadSalesOrderCart();
      return;
    }

    loadInvoicingCart();
  }, [enabled, source, liveOrderPayload, loadInvoicingCart, loadSalesOrderCart]);

  const updateCartPayload = useCallback((updater, options = {}) => {
    const { markDirty = true } = options;
    const currentPayload = cartPayloadRef.current;
    const nextPayload = typeof updater === 'function'
      ? updater(currentPayload)
      : updater;
    cartPayloadRef.current = nextPayload;
    setCartPayload(nextPayload);

    if (markDirty && (source === INVOICING_SOURCE || (source === SALES_ORDER_SOURCE && salesOrderCartUnlocked))) {
      mutationVersionRef.current += 1;
      setDirty(true);
    }

    return nextPayload;
  }, [salesOrderCartUnlocked, source]);

  const savePayload = useCallback(async (payloadToSave = cartPayloadRef.current) => {
    if (source !== INVOICING_SOURCE) return null;
    const normalized = normalizeCartPayload(
      payloadToSave,
      { meta: { source: INVOICING_SOURCE } },
      INVOICING_SOURCE
    );
    if (!invoicingCartKey) throw new Error('Open an existing invoice before editing its Invoicing Cart.');
    const result = await saveInvoicingCartByKey(orderId, invoicingCartKey, normalized, {
      expectedRevisionNumber: invoicingRevisionRef.current,
    });
    const savedRevision = Number(result?.cart?.revisionNumber || 0);
    if (savedRevision > 0) invoicingRevisionRef.current = savedRevision;
    cartPayloadRef.current = normalized;
    setCartPayload(normalized);
    setDirty(false);
    return result;
  }, [invoicingCartKey, orderId, source]);

  const save = useCallback(async () => {
    if (source === SALES_ORDER_SOURCE) {
      const message = salesOrderCartUnlocked
        ? 'Changes are staged. Use Actions → Regenerate Sales Order to apply them.'
        : 'Sales Order Cart is read-only.';
      setError(message);
      return { ok: false, message };
    }

    if (source === ORDER_SOURCE) {
      if (typeof fetchOrder === 'function') await fetchOrder();
      return { ok: true, message: 'Order cart refresh complete.' };
    }

    await savePayload(cartPayloadRef.current);

    return { ok: true, message: 'Invoicing cart saved.' };
  }, [fetchOrder, salesOrderCartUnlocked, savePayload, source]);

  const createFromSalesOrder = useCallback(async () => {
    const message = 'An Invoicing Cart is created with its invoice. Open or create an invoice first.';
    setError(message);
    return { ok: false, message };
  }, []);

  const diff = useCallback(async () => {
    if (source !== INVOICING_SOURCE) {
      const message = 'diff is only available for invoicing-cart.';
      setError(message);
      return null;
    }

    return fetchInvoicingCartDiff(orderId, invoicingCartKey);
  }, [orderId, source]);


  const resetDirty = useCallback(() => {
    mutationVersionRef.current += 1;
    setDirty(false);
  }, []);

  const orderForView = useMemo(
    () => buildOrderForCartView(order || {}, displayedPayload),
    [order, displayedPayload]
  );

  const mutationHandlers = useMemo(() => {
    const rejectReadOnly = () => {
      const message = 'Sales order cart is read-only.';
      setError(message);
      return { ok: false, message };
    };

    const isUnlockedSalesOrderWorkspace = source === SALES_ORDER_SOURCE && salesOrderCartUnlocked;

    if (source === SALES_ORDER_SOURCE && !salesOrderCartUnlocked) {
      return {
        addProduct: rejectReadOnly,
        editProduct: rejectReadOnly,
        deleteProduct: rejectReadOnly,
        copyProduct: rejectReadOnly,
        reorderProducts: rejectReadOnly,
        addSection: rejectReadOnly,
        editSection: rejectReadOnly,
        deleteSection: rejectReadOnly,
        reorderLayout: rejectReadOnly,
        addOtherService: rejectReadOnly,
        editOtherService: rejectReadOnly,
        deleteOtherService: rejectReadOnly,
        addAdjustment: rejectReadOnly,
        editAdjustment: rejectReadOnly,
        deleteAdjustment: rejectReadOnly,
      };
    }

    if (source !== INVOICING_SOURCE && !isUnlockedSalesOrderWorkspace) {
      return {
        addProduct: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        editProduct: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        deleteProduct: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        copyProduct: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        reorderProducts: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        addSection: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        editSection: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        deleteSection: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        reorderLayout: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        addOtherService: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        editOtherService: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        deleteOtherService: () => ({ ok: true, message: 'Handled by order cart UI.' }),
        addAdjustment: () => ({ ok: false, message: 'Adjustments are only available on invoicing carts.' }),
        editAdjustment: () => ({ ok: false, message: 'Adjustments are only available on invoicing carts.' }),
        deleteAdjustment: () => ({ ok: false, message: 'Adjustments are only available on invoicing carts.' }),
      };
    }

    const applyPayload = (mutator) => {
      const nextPayload = mutator(cartPayloadRef.current);
      cartPayloadRef.current = nextPayload;
      setCartPayload(nextPayload);
      mutationVersionRef.current += 1;
      setDirty(true);
      return { ok: true, payload: nextPayload };
    };

    return {
      addProduct: (product) => applyPayload((current) => addProductToPayload(current, product)),
      editProduct: (identity, patchOrProduct) => {
        const result = deriveWorkspaceProductEdit(
          cartPayloadRef.current,
          identity,
          patchOrProduct,
        );
        if (!result.ok) return result;
        return applyPayload(() => result.payload);
      },
      deleteProduct: (identity) => applyPayload((current) => deleteProductFromPayload(current, identity)),
      copyProduct: (identity) => applyPayload((current) => copyProductInPayload(current, identity)),
      reorderProducts: (activeIdentity, overIdentity) => applyPayload((current) => reorderProductsInPayload(current, activeIdentity, overIdentity)),
      addSection: (title, style) => applyPayload((current) => addSectionToPayload(current, title, style)),
      editSection: (identity, title, style) => applyPayload((current) => updateSectionInPayload(current, identity, title, style)),
      deleteSection: (identity) => applyPayload((current) => deleteSectionFromPayload(current, identity)),
      reorderLayout: (activeLayoutId, overLayoutId) => applyPayload((current) => reorderCartLayoutInPayload(current, activeLayoutId, overLayoutId)),
      addOtherService: (service) => applyPayload((current) => addOtherServiceToPayload(current, service)),
      editOtherService: (identity, patchOrService) => applyPayload((current) => updateOtherServiceInPayload(current, identity, patchOrService)),
      deleteOtherService: (identity) => applyPayload((current) => deleteOtherServiceFromPayload(current, identity)),
      addAdjustment: (adjustment) => applyPayload((current) => addAdjustmentToPayload(current, adjustment)),
      editAdjustment: (adjustment) => applyPayload((current) => updateAdjustmentInPayload(current, adjustment)),
      deleteAdjustment: (identity) => applyPayload((current) => deleteAdjustmentFromPayload(current, identity)),
    };
  }, [salesOrderCartUnlocked, source]);

  return {
    source,
    editable,
    loading: loading || sourceChangePending,
    error,
    missing,
    dirty,
    // Only reveal the loaded invoice cart when the selected key/order match.
    // Prevent showing a previous invoice's rows during an async cart switch.
    loadedInvoicingCartKey: loadedInvoicingCart.orderId === String(orderId) &&
      loadedInvoicingCart.key === invoicingCartKey && enabled && source === INVOICING_SOURCE
      ? loadedInvoicingCart.key : null,
    cartPayload: { ...displayedPayload, meta: { ...(displayedPayload?.meta || {}), dirty } },
    orderForView,
    updateCartPayload,
    resetDirty,
    reload,
    save,
    savePayload,
    createFromSalesOrder,
    diff,
    mutationHandlers,
    salesOrderCartUnlocked,
  };
}
